import './order-location-bar.css';

import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { OrderType, Pages, StoreListType } from '../../../types/enums';
import { IAddressFormat } from '../../../interfaces/global.interfaces';
import { ProfileService } from '../../../services/profile-service';
import { Data } from '../../../models/data';
import { OrderService } from '../../../services/order-service';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';

interface IOrderLocationBar {
    address: IAddressFormat | null,
    deliveryStoreAddress: IAddressFormat | null,
    selectedStoreKey: string,
    showArrow: boolean
}

/**
 * Represents the landing page of the application.
 */
export class OrderLocationBar {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _orderService: OrderService;
    private readonly _profileService: ProfileService;

    private readonly _containerElement: HTMLElement;
    private readonly _orderTypeElement: HTMLElement;
    private readonly _addressElement: HTMLElement;
    private readonly _deliveryStoreElement: HTMLElement;
    private readonly _state: IOrderLocationBar;

    /**
     * Creates an instance of the Landing page.
     * @param eventBus - The event bus for handling events.
     * @param dataService - The service managing application data.
     * @param orderService - The service handling order-related operations.
     * @param profileService - The service managing user profile operations.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, orderService: OrderService, profileService: ProfileService, showArrow: boolean = false) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._orderService = orderService;
        this._profileService = profileService;
        this._state = this._initializeModel(showArrow);

        this._setupEventBusListeners();
        this._containerElement = this._createElement();

        this._orderTypeElement = this._containerElement.querySelector("#span_order_location_bar_orderType")!;
        this._addressElement = this._containerElement.querySelector("#span_order_location_bar_address")!;
        this._deliveryStoreElement = this._containerElement.querySelector("#span_order_location_bar_delivery_store")!;
    }

    private _initializeModel(showArrow: boolean): IOrderLocationBar {
        return {
            address: null,
            deliveryStoreAddress: null,
            selectedStoreKey: "",
            showArrow: showArrow
        };
    }

    /**
     * Sets up event listeners for handling various application events.
     */
    private _setupEventBusListeners() {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, () => this._updateComponent());
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, () => this._updateComponent());
        this._eventBus.on(EventTypes.STORE_CHANGED, () => this._handleStoreChange());
    }

    public get element(): HTMLElement {
        return this._containerElement;
    }

    public set storeKey(storeKey: string) {
        this._state.selectedStoreKey = storeKey;
    }

    public updateAddress(address: IAddressFormat | null): void {
        this._state.address = address;
        this._updateComponent();
    }

    private _createElement(): HTMLElement {
        const contentFolderUrl = this._context.getProperty("publicStorageUrl");
        const orderTypeName = this._getOrderTypeName();
        const addressName = this._getAddress(this._state.address);

        const html = `
            <button class="location-info-bar">
                <img src="${contentFolderUrl}/online3/svg/location_pinpoint.svg" alt="Location Icon">
                <span>
                    <span>
                    <span id="span_order_location_bar_orderType">${orderTypeName}</span>
                    
                    <span id="span_order_location_bar_address">${addressName}</span>
                    </span>
                    <span id="span_order_location_bar_delivery_store"></span>
                </span>
                ${this._state.showArrow ? `<i class="icon-arrow-back" aria-hidden="true"></i>` : ""}
            </button>`;

        const element = Util.createHtmlElementFromTemplate(html);
        element.onclick = () => {
            this._handleClick();
        };

        return element;
    }

    private _updateComponent(): void {
        const orderTypeId = this._getSelectedOrderTypeId();
        const orderTypeName = this._getOrderTypeName();
        this._orderTypeElement.textContent = orderTypeName;

        const addressName = this._getAddress(this._state.address);
        this._addressElement.textContent = addressName;

        if (this._data.getBrandProperty("Settings").OOS.showDeliveryStoreAddress) {
            const deliveryStoreName = orderTypeId === OrderType.DELIVERY ? this._getAddress(this._state.deliveryStoreAddress) : "";
            this._deliveryStoreElement.textContent = deliveryStoreName;
        }
    }

    private _getOrderTypeName(): string {
        const orderTypeId = this._getSelectedOrderTypeId();

        switch (orderTypeId) {
            case OrderType.DINE_IN:
                return this._localizationService.names("DineInAt");
            case OrderType.TAKE_OUT:
                return this._localizationService.names("PickUpFrom");
            case OrderType.DELIVERY:
                return this._localizationService.names("DeliverTo");
        }

        return "";
    }

    private _getAddress(address: IAddressFormat | null): string {
        if (!address)
            return "";

        const addressObject = Util.formatAddressObject(address, this._data.getBrandProperty("Countries"));
        return addressObject.address1;
    }

    private readonly _handleClick = (): void => {
        const orderTypeId = this._getSelectedOrderTypeId();
        let addressId: number | null = null;

        if (orderTypeId === OrderType.DELIVERY) {
            const primaryAddress = this._profileService.user.getProperty("Addresses")?.find(x => x.IPRM);
            addressId = primaryAddress?.AID ?? null;
        }

        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, {
            orderTypeId: orderTypeId,
            selectedStoreKey: this._state.selectedStoreKey,
            addressId: addressId,
            forChangingStore: true,
            nearbyOnly: false,
            storeListType: StoreListType.NEARBY
        });
    }

    /**
     * Gets the currently selected order type ID or defaults to TAKE_OUT.
     * @returns The selected order type ID.
     */
    private _getSelectedOrderTypeId(): OrderType {
        return this._orderService.order.getProperty("OrderTypeID") ?? this._context.getProperty("defaultOrderType");
    }

    private readonly _handleStoreChange = (): void => {
        const orderTypeId = this._getSelectedOrderTypeId();

        switch (orderTypeId) {
            case OrderType.DINE_IN:
            case OrderType.TAKE_OUT:
                this._storeChange();
                break;
            case OrderType.DELIVERY:
                this._storeChangeDelivery();
                this._storeChange(true);
                break;
        }
    }

    private _storeChange(isDelivery: boolean = false): void {
        const storeInfo = this._data.getProperty("StoreInfo");

        const tempAddress: IAddressFormat = {
            StreetNo: storeInfo.streetNumber,
            Address1: storeInfo.address1,
            Address2: storeInfo.address2,
            Address3: "",
            Address4: "",
            Address5: "",
            City: "",
            State: "",
            ZipCode: "",
            CountryID: storeInfo.countryId || 1,
            AddressTypeID: 0
        };

        if (isDelivery)
            this._state.deliveryStoreAddress = tempAddress
        else
            this._state.address = tempAddress;

        this._updateComponent();
    }

    private _storeChangeDelivery(): void {
        const deliveryAddress = this._orderService.order.getProperty("Address");
        if (deliveryAddress) {
            const tempAddress: IAddressFormat = {
                StreetNo: deliveryAddress.StreetNo,
                Address1: deliveryAddress.StreetName,
                Address2: deliveryAddress.Address2,
                Address3: deliveryAddress.Address3,
                Address4: deliveryAddress.Address4,
                Address5: deliveryAddress.Address5,
                City: deliveryAddress.City,
                State: deliveryAddress.State,
                ZipCode: deliveryAddress.Zip,
                CountryID: deliveryAddress.CountryID || 1,
                AddressTypeID: 0
            };
            this._state.address = tempAddress;
        } else
            this._state.address = null;

        this._updateComponent();
    }
}